// SubscriptionBanner - Shows subscription status and expiry warnings
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {
  Crown,
  Lightning,
  Warning,
  Clock,
  X,
  ArrowRight,
  ShieldCheck,
  Sparkle
} from '@phosphor-icons/react'
import { useAuth } from '../contexts/AuthContext'
import { cn } from '../lib/utils'

interface SubscriptionBannerProps {
  variant?: 'full' | 'compact' | 'notification'
  onUpgrade?: () => void
  className?: string
}

const formatExpiryDate = (dateString?: string): string => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''

  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

export const SubscriptionBanner: React.FC<SubscriptionBannerProps> = ({
  variant = 'compact',
  onUpgrade,
  className
}) => {
  const navigate = useNavigate()
  const { subscriptionState, subscription } = useAuth()
  const expiryDate = subscriptionState.isTrialing
    ? subscription?.trialEndDate
    : subscription?.subscriptionEndDate || subscription?.trialEndDate
  const formattedExpiryDate = formatExpiryDate(expiryDate)
  const expiryTitle = formattedExpiryDate
    ? `Expires on ${formattedExpiryDate}`
    : undefined
  const expiryTimingText = subscriptionState.daysRemaining === 0
    ? 'expires today'
    : `expires in ${subscriptionState.daysRemaining} day${subscriptionState.daysRemaining === 1 ? '' : 's'}`

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade()
    } else {
      navigate('/settings?tab=subscription')
    }
  }

  // Don't show if active subscription with plenty of time
  if (variant !== 'notification' && subscriptionState.isActive && subscriptionState.daysRemaining > 7) {
    return null
  }

  // Compact badge for profile/header - already responsive
  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          "inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium cursor-pointer transition-all",
          subscriptionState.isTrialing && subscriptionState.daysRemaining > 3
            ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
            : subscriptionState.isTrialing && subscriptionState.daysRemaining <= 3
            ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
            : subscriptionState.isGracePeriod
            ? "bg-red-100 text-red-700 hover:bg-red-200"
            : subscriptionState.isExpired
            ? "bg-red-100 text-red-700 hover:bg-red-200 animate-pulse"
            : "bg-green-100 text-green-700",
          className
        )}
        onClick={handleUpgrade}
        title={expiryTitle}
      >
        {subscriptionState.isTrialing && (
          <>
            <Clock size={14} className="sm:hidden" weight="fill" />
            <Clock size={16} className="hidden sm:block" weight="fill" />
            <span className="truncate max-w-[120px] sm:max-w-none">
              <span className="sm:hidden">Exp {subscriptionState.daysRemaining}d</span>
              <span className="hidden sm:inline">Trial expires in {subscriptionState.daysRemaining}d</span>
            </span>
          </>
        )}
        {subscriptionState.isGracePeriod && (
          <>
            <Warning size={14} className="sm:hidden" weight="fill" />
            <Warning size={16} className="hidden sm:block" weight="fill" />
            <span className="truncate max-w-[100px] sm:max-w-none">
              Grace
              <span className="hidden sm:inline"> period</span>
            </span>
          </>
        )}
        {subscriptionState.isExpired && (
          <>
            <Warning size={14} className="sm:hidden" weight="fill" />
            <Warning size={16} className="hidden sm:block" weight="fill" />
            <span className="truncate max-w-[100px] sm:max-w-none">
              Expired
              <span className="hidden sm:inline"> - Upgrade</span>
            </span>
          </>
        )}
        {subscriptionState.isActive && subscriptionState.daysRemaining <= 7 && (
          <>
            <Clock size={14} className="sm:hidden" weight="fill" />
            <Clock size={16} className="hidden sm:block" weight="fill" />
            <span className="truncate max-w-[120px] sm:max-w-none">
              <span className="sm:hidden">Exp {subscriptionState.daysRemaining}d</span>
              <span className="hidden sm:inline">Expires in {subscriptionState.daysRemaining}d</span>
            </span>
          </>
        )}
      </motion.div>
    )
  }

  // Full banner for dashboard/profile - fully responsive
  if (variant === 'full') {
    const isUrgent = subscriptionState.daysRemaining <= 3 || subscriptionState.isExpired || subscriptionState.isGracePeriod

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className={cn(
          "rounded-xl p-3 sm:p-4 md:p-5 border",
          isUrgent
            ? "bg-gradient-to-r from-red-50 to-orange-50 border-red-200"
            : "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200",
          className
        )}
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          {/* Icon - hidden on smallest screens, visible on sm and up */}
          <div className={cn(
            "hidden sm:flex p-2 md:p-3 rounded-xl shrink-0",
            isUrgent ? "bg-red-100" : "bg-blue-100"
          )}>
            {subscriptionState.isExpired ? (
              <>
                <Warning size={20} className="md:hidden" weight="duotone" color="#dc2626" />
                <Warning size={28} className="hidden md:block" weight="duotone" color="#dc2626" />
              </>
            ) : subscriptionState.isTrialing ? (
              <>
                <Clock size={20} className="md:hidden" weight="duotone" color={isUrgent ? "#dc2626" : "#2563eb"} />
                <Clock size={28} className="hidden md:block" weight="duotone" color={isUrgent ? "#dc2626" : "#2563eb"} />
              </>
            ) : (
              <>
                <Crown size={20} className="md:hidden" weight="duotone" color="#2563eb" />
                <Crown size={28} className="hidden md:block" weight="duotone" color="#2563eb" />
              </>
            )}
          </div>

          {/* Content - takes full width on mobile */}
          <div className="flex-1 w-full sm:w-auto">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className={cn(
                "text-sm sm:text-base md:text-lg font-semibold",
                isUrgent ? "text-red-800" : "text-blue-800"
              )}>
                {subscriptionState.isExpired
                  ? "Expired"
                  : subscriptionState.isGracePeriod
                  ? "Grace Period"
                  : subscriptionState.isTrialing
                  ? `Trial expires in ${subscriptionState.daysRemaining}d`
                  : `Expires in ${subscriptionState.daysRemaining}d`
                }
                <span className="hidden sm:inline ml-1">
                  {subscriptionState.isExpired
                    ? " - Subscription Expired"
                    : subscriptionState.isGracePeriod
                    ? " - Action Required"
                    : subscriptionState.isTrialing
                    ? " - Free Trial"
                    : " - Subscription"
                  }
                </span>
              </h3>
              {subscriptionState.isTrialing && (
                <span className="px-2 py-0.5 bg-blue-200 text-blue-800 text-[10px] sm:text-xs font-medium rounded-full whitespace-nowrap">
                  Trial
                </span>
              )}
            </div>
            
            {/* Description - hidden on smallest screens, shows on sm and up */}
            <p className={cn(
              "hidden sm:block text-xs md:text-sm",
              isUrgent ? "text-red-700" : "text-blue-700"
            )}>
              {subscriptionState.isExpired
                ? "View-only mode. Upgrade to continue editing."
                : subscriptionState.isGracePeriod
                ? "Pay within 3 days to avoid losing edit access."
                : subscriptionState.daysRemaining <= 3
                ? `Expires${formattedExpiryDate ? ` on ${formattedExpiryDate}` : ' soon'}. Upgrade now to continue without interruption.`
                : `Remember: this plan expires${formattedExpiryDate ? ` on ${formattedExpiryDate}` : ' soon'}. Upgrade to continue.`
              }
            </p>
            
            {/* Mobile-only compact description */}
            <p className={cn(
              "sm:hidden text-xs mt-1",
              isUrgent ? "text-red-700" : "text-blue-700"
            )}>
              {subscriptionState.isExpired
                ? "View-only mode"
                : subscriptionState.isGracePeriod
                ? "Action required"
                : subscriptionState.daysRemaining <= 3
                ? "Expires soon"
                : formattedExpiryDate || "Expiry reminder"
              }
            </p>
          </div>

          {/* Button - full width on mobile, auto on sm and up */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleUpgrade}
            className={cn(
              "w-full sm:w-auto flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-xl font-semibold text-xs sm:text-sm shadow-lg hover:shadow-xl transition-all whitespace-nowrap",
              isUrgent
                ? "bg-gradient-to-r from-red-500 to-orange-500 text-white"
                : "bg-gradient-to-r from-blue-500 to-indigo-500 text-white"
            )}
          >
            <Lightning size={14} className="sm:hidden" weight="fill" />
            <Lightning size={18} className="hidden sm:block" weight="fill" />
            {subscriptionState.isExpired ? "Activate" : "Upgrade"}
            <span className="hidden sm:inline">
              {subscriptionState.isExpired ? " Now" : " to Pro"}
            </span>
          </motion.button>
        </div>
      </motion.div>
    )
  }

  if (variant === 'notification') {
    return (
      <motion.button
        type="button"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={handleUpgrade}
        className={cn(
          "w-full bg-amber-500 text-white px-3 sm:px-4 py-2 flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-3 text-xs sm:text-sm text-center sm:text-left hover:bg-amber-600 transition-colors",
          className
        )}
        title={expiryTitle}
      >
        <span className="flex items-center gap-2 font-semibold">
          <Clock size={14} className="sm:hidden shrink-0" weight="fill" />
          <Clock size={18} className="hidden sm:block shrink-0" weight="fill" />
          {subscriptionState.isTrialing ? 'Trial' : 'Subscription'} {expiryTimingText}
        </span>
        <span className="text-white/90 truncate max-w-[92vw] sm:max-w-[560px]">
          Remember{formattedExpiryDate ? `: expiry date is ${formattedExpiryDate}` : ' to renew before expiry'}.
        </span>
      </motion.button>
    )
  }

  return null
}

// Login notification modal - forcefully centered
interface SubscriptionNotificationProps {
  isOpen: boolean
  onClose: () => void
  onUpgrade: () => void
}

export const SubscriptionNotification: React.FC<SubscriptionNotificationProps> = ({
  isOpen,
  onClose,
  onUpgrade
}) => {
  const { subscriptionState } = useAuth()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  if (!isOpen || !mounted) return null

  const isUrgent = subscriptionState.daysRemaining <= 3 || subscriptionState.isExpired

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[9999]"
            onClick={onClose}
          />

          {/* Modal Container - using flexbox for guaranteed centering */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none"
          >
            {/* Modal Content - pointer-events-auto to allow interaction */}
            <div 
              className="pointer-events-auto w-full max-w-md mx-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
                {/* Header with gradient */}
                <div className={cn(
                  "p-4 sm:p-6 text-white relative overflow-hidden",
                  isUrgent
                    ? "bg-gradient-to-br from-red-500 to-orange-500"
                    : "bg-gradient-to-br from-blue-500 to-indigo-600"
                )}>
                  {/* Close button */}
                  <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 p-1.5 sm:p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                    aria-label="Close"
                  >
                    <X size={16} className="sm:hidden" weight="bold" />
                    <X size={18} className="hidden sm:block" weight="bold" />
                  </button>

                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-24 sm:w-32 h-24 sm:h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                  <div className="absolute bottom-0 left-0 w-16 sm:w-20 h-16 sm:h-20 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" />

                  <div className="relative flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/20 rounded-xl shrink-0">
                      {subscriptionState.isExpired ? (
                        <>
                          <Warning size={24} className="sm:hidden" weight="duotone" />
                          <Warning size={32} className="hidden sm:block" weight="duotone" />
                        </>
                      ) : (
                        <>
                          <Clock size={24} className="sm:hidden" weight="duotone" />
                          <Clock size={32} className="hidden sm:block" weight="duotone" />
                        </>
                      )}
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-lg sm:text-xl font-bold truncate">
                        {subscriptionState.isExpired
                          ? "Subscription Expired"
                          : `${subscriptionState.daysRemaining} Days Remaining`
                        }
                      </h2>
                      <p className="text-white/80 text-xs sm:text-sm">
                        {subscriptionState.isTrialing ? "Free Trial" : "Subscription"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6">
                  <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
                    {subscriptionState.isExpired
                      ? "Your subscription has expired. Upgrade now to restore full access to all features."
                      : subscriptionState.daysRemaining <= 3
                      ? "Your trial is ending very soon! Upgrade now to continue using all features without interruption."
                      : "Your free trial is ending soon. Upgrade to Pro to continue enjoying unlimited invoices, reports, and more."
                    }
                  </p>

                  {/* Pricing - responsive grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 sm:mb-6">
                    {/* Monthly Plan */}
                    <div className="bg-slate-50 rounded-xl p-3 sm:p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-600 text-sm sm:text-base">Monthly</span>
                        <span className="font-bold text-base sm:text-lg">₹499</span>
                      </div>
                      <span className="text-xs sm:text-sm text-slate-400">per month</span>
                    </div>

                    {/* Yearly Plan */}
                    <div className="bg-slate-50 rounded-xl p-3 sm:p-4 border-2 border-green-200 relative">
                      <div className="absolute -top-2 left-3 px-2 py-0.5 bg-green-100 text-green-700 text-[10px] sm:text-xs font-medium rounded-full">
                        Save ₹989
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-600 text-sm sm:text-base">Yearly</span>
                        <span className="font-bold text-base sm:text-lg">₹4,999</span>
                      </div>
                      <span className="text-xs sm:text-sm text-slate-400">₹416/month</span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2 mb-4 sm:mb-6">
                    {[
                      "Unlimited invoices & quotations",
                      "Advanced reports & analytics",
                      "WhatsApp integration",
                      "Priority support"
                    ].map((feature, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs sm:text-sm text-slate-600">
                        <ShieldCheck size={14} className="sm:hidden shrink-0 mt-0.5 text-green-500" weight="fill" />
                        <ShieldCheck size={16} className="hidden sm:block shrink-0 mt-0.5 text-green-500" weight="fill" />
                        <span className="flex-1">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Actions - stacked on mobile, side by side on sm and up */}
                  <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="w-full sm:flex-1 px-4 py-2.5 sm:py-2.5 border border-slate-200 rounded-xl font-medium text-slate-600 hover:bg-slate-50 transition-colors text-sm sm:text-base"
                    >
                      Remind Later
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={onUpgrade}
                      className={cn(
                        "w-full sm:flex-1 flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2.5 rounded-xl font-semibold text-white shadow-lg transition-all text-sm sm:text-base",
                        isUrgent
                          ? "bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                          : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600"
                      )}
                    >
                      <Sparkle size={16} className="sm:hidden" weight="fill" />
                      <Sparkle size={18} className="hidden sm:block" weight="fill" />
                      Upgrade Now
                    </motion.button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
// View-only mode banner - fully responsive
export const ViewOnlyBanner: React.FC<{ className?: string }> = ({ className }) => {
  const navigate = useNavigate()
  const { subscriptionState } = useAuth()

  if (!subscriptionState.isExpired) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "bg-red-500 text-white px-3 sm:px-4 py-2 sm:py-2 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-xs sm:text-sm text-center sm:text-left",
        className
      )}
    >
      <div className="flex items-center gap-2">
        <Warning size={14} className="sm:hidden shrink-0" weight="fill" />
        <Warning size={18} className="hidden sm:block shrink-0" weight="fill" />
        <span className="font-medium">
          <span className="sm:hidden">View-only mode</span>
          <span className="hidden sm:inline">View-only mode - Your subscription has expired</span>
        </span>
      </div>
      <button
        onClick={() => navigate('/settings?tab=subscription')}
        className="flex items-center justify-center gap-1 px-3 py-1 bg-white text-red-600 rounded-full font-semibold text-xs hover:bg-red-50 transition-colors whitespace-nowrap"
      >
        Upgrade <ArrowRight size={10} className="sm:hidden" weight="bold" />
        <ArrowRight size={12} className="hidden sm:block" weight="bold" />
      </button>
    </motion.div>
  )
}

export default SubscriptionBanner
